import { AsyncStorageStatic } from '@react-native-async-storage/async-storage';
import sagaMonitor from '@redux-saga/simple-saga-monitor';
import { isWeb } from 'affiny-core/helpers/targetCheck/targetCheck';
import { parseHostUrl } from 'affiny-core/services/config/parser';
import union from 'lodash/union';
import { applyMiddleware, createStore as reduxCreateStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import {
  LocalForageStorage,
  PersistConfig,
  persistReducer,
  persistStore,
} from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import createSagaMiddleware from 'redux-saga';
import { logger } from '../logger';
import { DangerousStore } from './dangerousStore';
import { rootReducer } from './reducer';
import { rootSaga as coreRootSaga } from './saga';
import { IRootState } from './state';

// module.hot come from webpack & react-native metro bundler
declare let module: {
  hot: {
    accept(callback?: () => void): void;
  };
};

interface IPersistConfig {
  whitelist: Array<keyof IRootState>;
}
export interface ICreateAppStore {
  storage: LocalForageStorage | AsyncStorageStatic;
  createStore?: any;
  rootSaga?: any;
  persitenceConfig?: IPersistConfig;
}

const DEFAULT_PERSIST_WHITELIST: Array<keyof IRootState> = [
  'subscription',
  'reflists',
];

export function createAppStore({
  storage,
  createStore = reduxCreateStore,
  rootSaga = coreRootSaga,
  persitenceConfig = { whitelist: [] },
}: ICreateAppStore) {
  const context = isWeb()
    ? parseHostUrl(window.location.hostname)
    : { namespace: '' };

  const persistConfig: PersistConfig = {
    key: 'root',
    storage,
    // namespace is only on Kube
    // no persist API (empty array) because we want to see API errors on Kube
    whitelist: context.namespace
      ? []
      : union(DEFAULT_PERSIST_WHITELIST, persitenceConfig.whitelist),
    timeout: undefined,
  };
  const persistedReducer = persistReducer(persistConfig, rootReducer);
  const sagaMiddleware = createSagaMiddleware({
    sagaMonitor,
    onError: (error, info) => {
      logger.handleException(error, { rethrow: false, extraInfo: info });
    },
  });
  const middlewares = [sagaMiddleware];
  const store = createStore(
    persistedReducer,
    composeWithDevTools(applyMiddleware(...middlewares))
  );

  const persistedStore = persistStore(store);

  sagaMiddleware.run(rootSaga);

  if (module.hot) {
    module.hot.accept(() => {
      const nextRootReducer = rootReducer;

      store.replaceReducer(nextRootReducer);
    });
  }

  DangerousStore.store = store;
  DangerousStore.persistor = persistedStore;

  return { store, persistedStore, PersistGate };
}

export { connect, Provider } from 'react-redux';
